"use client";
import { useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/authContext";
import { useMutation } from "@apollo/client";
import { ADD_SUBSCRIPTION } from "@/api/apollo-client/mutations/mutations";
import { initializeApp } from "firebase/app";
import { getMessaging, getToken } from "firebase/messaging";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

export default function PushPermissionSubscriber() {
  const { user } = useAuth();
  const [addSubscription] = useMutation(ADD_SUBSCRIPTION);

  const subscribe = useCallback(
    async (token: string) => {
      if (!process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY) {
        console.error("VAPID public key is not set in environment variables.");
        return;
      }

      if (!user?.id) {
        console.error("User ID is not available for subscription");
        return;
      }

      await addSubscription({
        variables: {
          input: {
            user_id: user.id,
            subscription: token,
            additional_info: "firebase subscription",
            subscription_action: "ADD",
            device_id: token,
          },
        },
      });
    },
    [addSubscription, user?.id]
  );

  const initializeToken = useCallback(async () => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

    try {
      // Check if permission is already granted
      let permission = Notification.permission;

      // Request permission for non-iOS platforms if not already granted
      if (permission !== "granted") {
        // Detect iOS
        const isIOS =
          /iPad|iPhone|iPod/.test(navigator.userAgent) ||
          (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

        if (!isIOS) {
          permission = await Notification.requestPermission();
        }

        if (permission !== "granted") {
          return;
        }
      }

      if (!user?.id) {
        return;
      }

      const messaging = getMessaging(app);
      const currentUserId = user.id;
      const tokenKey = `${currentUserId}_fcm_token`;
      const cached = localStorage.getItem(tokenKey);

      if (!cached) {
        const currentToken = await getToken(messaging, {
          vapidKey: process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY,
        });
        if (currentToken) {
          try {
            await subscribe(currentToken);
            localStorage.setItem(tokenKey, currentToken);
          } catch (error) {
            console.error(
              "PushPermissionSubscriber - Failed to register token:",
              error
            );
            // Don't cache if registration failed
          }
        } else {
        }
      } else {
        // Re-register cached token to ensure backend has it
        try {
          await subscribe(cached);
        } catch (error) {
          console.error(
            "PushPermissionSubscriber - Failed to re-register cached token:",
            error
          );
          // Clear invalid cached token
          localStorage.removeItem(tokenKey);
        }
      }
    } catch (err) {
      console.error("Error getting FCM token:", err);
    }
  }, [subscribe, user?.id]);

  useEffect(() => {
    // Only initialize when user is available
    if (user?.id) {
      void initializeToken();
    } else {
    }
  }, [initializeToken, user?.id]);

  return null;
}
